import { useTranslation } from "react-i18next";
import { Mail, Phone, ArrowRight, Building2, Layers, RefreshCcw, Compass, Eye, LayoutDashboard, MousePointer2 } from "lucide-react";
import ContactForm from "@/components/ContactForm";

const IMG_HERO = "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1600&q=80";
const IMG_INTRO = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1400&q=80";
const IMG_PROJECTS_1 = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=900&q=80";
const IMG_PROJECTS_2 = "https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=900&q=80";
const IMG_PROJECTS_3 = "https://images.unsplash.com/photo-1554435493-93422e8220c8?auto=format&fit=crop&w=900&q=80";
const IMG_PROJECTS_4 = "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=900&q=80";
const IMG_HOW = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1400&q=80";

const projectIcons = [Building2, RefreshCcw, Layers, Compass];
const principleIcons = [Eye, LayoutDashboard, MousePointer2];

export default function Home() {
  const { t } = useTranslation();

  const openContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openHow = (e) => {
    e.preventDefault();
    const el = document.getElementById("how");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="home" data-testid="home-page">
      {/* HERO */}
      <section className="hero" id="hero">
        <div className="hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">{t("hero.eyebrow")}</span>
            <h1 className="h-display">{t("hero.title")}</h1>
            <p className="lede">{t("hero.subtitle")}</p>
            <div className="hero-ctas">
              <a href="#contact" onClick={openContact} className="btn btn-solid" data-testid="hero-cta-primary">
                {t("hero.ctaPrimary")} <ArrowRight size={16} />
              </a>
              <a href="#how" onClick={openHow} className="btn btn-outline" data-testid="hero-cta-secondary">
                {t("hero.ctaSecondary")}
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat"><span className="stat-value">{t("hero.stat1Value")}</span><span className="stat-label">{t("hero.stat1Label")}</span></div>
              <div className="stat"><span className="stat-value">{t("hero.stat2Value")}</span><span className="stat-label">{t("hero.stat2Label")}</span></div>
              <div className="stat"><span className="stat-value">{t("hero.stat3Value")}</span><span className="stat-label">{t("hero.stat3Label")}</span></div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-image-frame">
              <img src={IMG_HERO} alt="Buenos Aires skyline with residential towers" loading="eager" />
            </div>
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              CABA · Córdoba · Rosario
            </div>
          </div>
        </div>
      </section>

      {/* INTRO / ABOUT */}
      <section className="section section-alt" id="about">
        <div className="section-grid">
          <div className="section-side">
            <span className="eyebrow">{t("intro.eyebrow")}</span>
            <h2 className="h-section">{t("intro.title")}</h2>
            <div className="intro-image">
              <img src={IMG_INTRO} alt="Residential building façade in Argentina" loading="lazy" />
            </div>
          </div>
          <div className="section-body">
            <p className="body-para">{t("intro.p1")}</p>
            <p className="body-para">{t("intro.p2")}</p>
            <p className="body-para">{t("intro.p3")}</p>
            <p className="body-para">{t("intro.p4")}</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section" id="how">
        <div className="section-header">
          <span className="eyebrow">{t("how.eyebrow")}</span>
          <h2 className="h-section">{t("how.title")}</h2>
        </div>

        <div className="how-steps">
          <div className="how-step"><h3 className="how-step-title">{t("how.step1Title")}</h3><p className="how-step-text">{t("how.step1Text")}</p></div>
          <div className="how-step"><h3 className="how-step-title">{t("how.step2Title")}</h3><p className="how-step-text">{t("how.step2Text")}</p></div>
          <div className="how-step"><h3 className="how-step-title">{t("how.step3Title")}</h3><p className="how-step-text">{t("how.step3Text")}</p></div>
          <div className="how-step"><h3 className="how-step-title">{t("how.step4Title")}</h3><p className="how-step-text">{t("how.step4Text")}</p></div>
        </div>

        <div className="how-body-grid">
          <div className="how-image">
            <img src={IMG_HOW} alt="Argentine high-rise residential towers under construction" loading="lazy" />
          </div>
          <div className="how-body">
            <p className="body-para">{t("how.p1")}</p>
            <p className="body-para">{t("how.p2")}</p>
            <p className="body-para">{t("how.p3")}</p>
            <p className="body-para">{t("how.p4")}</p>
            <p className="body-para">{t("how.p5")}</p>
            <p className="body-para">{t("how.p6")}</p>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section section-alt" id="projects">
        <div className="section-header">
          <span className="eyebrow">{t("projects.eyebrow")}</span>
          <h2 className="h-section">{t("projects.title")}</h2>
        </div>
        <div className="projects-grid">
          {[1, 2, 3, 4].map((n, i) => {
            const Icon = projectIcons[i];
            const img = [IMG_PROJECTS_1, IMG_PROJECTS_2, IMG_PROJECTS_3, IMG_PROJECTS_4][i];
            return (
              <article className="project-card" key={n}>
                <div className="project-img"><img src={img} alt="" loading="lazy" /></div>
                <div className="project-body">
                  <div className="project-icon"><Icon size={18} /></div>
                  <h3 className="project-title">{t(`projects.item${n}Title`)}</h3>
                  <p className="project-text">{t(`projects.item${n}Text`)}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="section" id="principles">
        <div className="section-header">
          <span className="eyebrow">{t("principles.eyebrow")}</span>
          <h2 className="h-section">{t("principles.title")}</h2>
        </div>
        <div className="principles-grid">
          {[1, 2, 3].map((n, i) => {
            const Icon = principleIcons[i];
            return (
              <div className="principle-card" key={n}>
                <div className="principle-icon"><Icon size={20} /></div>
                <h3 className="principle-title">{t(`principles.item${n}Title`)}</h3>
                <p className="principle-text">{t(`principles.item${n}Text`)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="section-narrow">
        <div className="disclaimer-card">
          <h3 className="disclaimer-title">{t("disclaimer.title")}</h3>
          <p className="disclaimer-text">{t("disclaimer.text")}</p>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section section-alt" id="contact">
        <div className="contact-grid">
          <div className="contact-side">
            <span className="eyebrow">{t("contact.eyebrow")}</span>
            <h2 className="h-section">{t("contact.title")}</h2>
            <p className="body-para">{t("contact.subtitle")}</p>
            <div className="contact-direct">
              <a href="mailto:hola@ronavirexa.com" className="contact-direct-link">
                <Mail size={16} /> <span>{t("contact.directEmail")} hola@ronavirexa.com</span>
              </a>
              <a href="tel:+541148920415" className="contact-direct-link">
                <Phone size={16} /> <span>{t("contact.directPhone")}: +54 11 4892-0415</span>
              </a>
            </div>
          </div>
          <div className="contact-form-wrap">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
